import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, photoUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MapView from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin, Clock, Hand, Trash2, CheckCircle2, Share2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  available: { label: "Available", cls: "bg-[#7C3AED]/10 text-[#7C3AED]" },
  claimed:   { label: "Claimed",   cls: "bg-[#E5B15D]/15 text-[#9c7426]" },
  gone:      { label: "Gone",      cls: "bg-[#A8A6A1]/20 text-[#6b6a66]" },
};

export default function ItemDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/items/${id}`);
      setItem(data);
    } catch {
      setItem(null);
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-[#6B7280]">Loading…</div>;
  if (!item) return (
    <div className="max-w-3xl mx-auto px-5 py-20 text-center">
      <h2 className="font-display text-2xl">Item not found</h2>
      <Link to="/browse" className="text-[#7C3AED] hover:underline mt-3 inline-block">← Back to browse</Link>
    </div>
  );

  const isOwner = user && user.id === item.user_id;
  const photos = item.photos || [];

  const doClaim = async () => {
    if (!user) { nav("/login", { state: { from: `/items/${item.id}` } }); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/items/${item.id}/claim`);
      setItem(data);
      toast.success("Claimed! Head over and grab it.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const markGone = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/items/${item.id}`, { status: "gone" });
      setItem(data);
      toast.success("Marked as gone");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const markAvailable = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/items/${item.id}`, { status: "available" });
      setItem(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deleteItem = async () => {
    if (!window.confirm("Delete this post?")) return;
    setBusy(true);
    try {
      await api.delete(`/items/${item.id}`);
      toast.success("Post deleted");
      nav("/profile");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <button onClick={() => nav(-1)} data-testid="back-btn"
          className="inline-flex items-center text-sm text-[#6B7280] hover:text-[#0F0F12] mb-5">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </button>
        <button data-testid="share-btn" onClick={async () => {
          const url = `${window.location.origin}/items/${item.id}`;
          const text = `Free on the curb: ${item.title} — ${item.address || "neighbourhood pickup"}`;
          if (navigator.share) {
            try { await navigator.share({ title: item.title, text, url }); return; }
            catch (err) {
              // AbortError = user dismissed the native share sheet; not an error.
              if (err?.name !== "AbortError") console.warn("share failed", err);
            }
          }
          try { await navigator.clipboard.writeText(url); toast.success("Link copied — share it with your neighbours!"); }
          catch (err) { console.warn("clipboard write failed", err); toast.error("Couldn't copy link"); }
        }}
          className="ml-3 inline-flex items-center text-sm text-[#7C3AED] hover:underline mb-5">
          <Share2 className="h-4 w-4 mr-1" /> Share
        </button>

        {/* Bento photo gallery */}
        {photos.length > 0 && (
          <div className="grid grid-cols-12 grid-rows-2 gap-3 h-[280px] sm:h-[420px] mb-8">
            <div className={`${photos.length === 1 ? "col-span-12" : "col-span-12 md:col-span-8"} row-span-2 rounded-2xl overflow-hidden bg-[#E8E5F0]`}>
              <img src={photoUrl(photos[0])} alt={item.title} className="w-full h-full object-cover" />
            </div>
            {photos.slice(1, 5).map((p, i) => (
              <div key={p}
                className={`col-span-6 md:col-span-4 ${photos.length <= 3 ? "row-span-2" : "row-span-1"} rounded-2xl overflow-hidden bg-[#E8E5F0] hidden md:block`}>
                <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS[item.status].cls}`} data-testid="status-badge">
                {STATUS[item.status].label}
              </span>
              <span className="text-xs uppercase tracking-[0.15em] text-[#7C3AED] font-medium">{item.category}</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight" data-testid="item-title">
              {item.title}
            </h1>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-[#6B7280]">
              <span className="inline-flex items-center"><MapPin className="h-4 w-4 mr-1" />{item.address || `${item.lat.toFixed(4)}, ${item.lng.toFixed(4)}`}</span>
              <span className="inline-flex items-center"><Clock className="h-4 w-4 mr-1" />{new Date(item.created_at).toLocaleString()}</span>
              <span>by <strong className="text-[#0F0F12]">{item.user_name}</strong></span>
            </div>

            {item.description && (
              <div className="mt-6 bg-white rounded-2xl border border-[#E8E5F0] p-6 whitespace-pre-line leading-relaxed">
                {item.description}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white rounded-3xl border border-[#E8E5F0] p-6 lg:sticky lg:top-20 space-y-4">
              <MapView items={[item]} center={[item.lat, item.lng]} zoom={15} className="h-[240px]" />

              {!isOwner && (
                <Button variant="outline" data-testid="message-poster-btn" disabled={busy}
                  className="w-full h-11 rounded-full border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white"
                  onClick={async () => {
                    if (!user) { nav("/login", { state: { from: `/items/${item.id}` } }); return; }
                    const body = window.prompt(`Send a message to ${item.user_name} about "${item.title}":`);
                    if (!body || !body.trim()) return;
                    setBusy(true);
                    try {
                      const { data } = await api.post(`/items/${item.id}/messages`, { body });
                      toast.success("Message sent");
                      nav(`/messages/${encodeURIComponent(data.thread_id)}`);
                    } catch (e) {
                      toast.error(formatApiError(e.response?.data?.detail));
                    } finally { setBusy(false); }
                  }}>
                  <MessageCircle className="h-4 w-4 mr-2" />Message {item.user_name}
                </Button>
              )}

              {!isOwner && item.status === "available" && (
                <Button onClick={doClaim} disabled={busy}
                  className="w-full h-12 rounded-full bg-[#7C3AED] hover:bg-[#5B21B6] text-white text-base"
                  data-testid="claim-btn">
                  <Hand className="h-4 w-4 mr-2" />I'm taking it
                </Button>
              )}
              {!isOwner && item.status === "claimed" && user && item.claimed_by === user.id && (
                <div className="space-y-2" data-testid="claimer-controls">
                  <div className="text-sm text-[#0F0F12] text-center font-medium">
                    You claimed this. Heading over soon?
                  </div>
                  <Button variant="outline" disabled={busy} data-testid="unclaim-btn"
                    onClick={async () => {
                      setBusy(true);
                      try {
                        const { data } = await api.post(`/items/${item.id}/unclaim`);
                        setItem(data);
                        toast.success("Unclaimed — others can grab it now.");
                      } catch (e) {
                        toast.error(formatApiError(e.response?.data?.detail));
                      } finally { setBusy(false); }
                    }}
                    className="w-full h-11 rounded-full">
                    I changed my mind — unclaim
                  </Button>
                </div>
              )}
              {!isOwner && item.status === "claimed" && (!user || item.claimed_by !== user.id) && (
                <div className="text-sm text-[#6B7280] text-center" data-testid="claimed-note">
                  Someone's already claimed this. Check the map for other items.
                </div>
              )}
              {!isOwner && item.status === "gone" && (
                <div className="text-sm text-[#6B7280] text-center">This one's gone — a happy ending!</div>
              )}

              {isOwner && (
                <div className="space-y-2" data-testid="owner-controls">
                  {item.status !== "available" && (
                    <Button variant="outline" onClick={markAvailable} disabled={busy}
                      className="w-full h-11 rounded-full" data-testid="mark-available">
                      Mark as available
                    </Button>
                  )}
                  {item.status !== "gone" && (
                    <Button onClick={markGone} disabled={busy}
                      className="w-full h-11 rounded-full bg-[#0F0F12] hover:bg-[#1F1F23] text-white"
                      data-testid="mark-gone">
                      <CheckCircle2 className="h-4 w-4 mr-2" />Mark as gone
                    </Button>
                  )}
                  <Button variant="outline" onClick={deleteItem} disabled={busy}
                    className="w-full h-11 rounded-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    data-testid="delete-item">
                    <Trash2 className="h-4 w-4 mr-2" />Delete post
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
